import type {Category, SessionUser, ShopSiteStatus} from "@corporatesclub/shared";
import {SESSION_IDLE_TTL_MS, getCategories, getHomeBannerCategories, getSession, getWishlist, type HomeBannerCategory} from "@corporatesclub/shared";

const SESSION_KEY = "cc-session";
const SESSION_TTL = SESSION_IDLE_TTL_MS;
const SITE_STATUS_KEY = "cc-shop-status";
const SITE_STATUS_TTL = 24 * 60 * 60 * 1000;

type SessionCache = {user: SessionUser; ts: number};

function readRawCache(): SessionCache | null {
  if(typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(SESSION_KEY) || sessionStorage.getItem(SESSION_KEY);
    if(!raw) return null;
    const parsed = JSON.parse(raw) as SessionCache;
    if(Date.now() - parsed.ts > SESSION_TTL) return null;
    return parsed;
  } catch {
    return null;
  }
}

function touchSessionCache(user: SessionUser) {
  if(typeof window === "undefined") return;
  try {
    const payload = JSON.stringify({user, ts: Date.now()} satisfies SessionCache);
    localStorage.setItem(SESSION_KEY, payload);
    sessionStorage.setItem(SESSION_KEY, payload);
  } catch {}
}

export function readSessionCache(): SessionUser | null {
  return readRawCache()?.user ?? null;
}

export function writeSessionCache(user: SessionUser) {
  touchSessionCache(user);
}

export function bumpSessionCache() {
  const cached = readRawCache();
  if(cached?.user.authenticated) touchSessionCache(cached.user);
}

export function clearSessionCache() {
  if(typeof window === "undefined") return;
  try {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  } catch {}
}

let sessionInflight: Promise<SessionUser> | null = null;

async function getSessionOnce(): Promise<SessionUser> {
  return getSession();
}

export function fetchSessionFresh(): Promise<SessionUser> {
  if(!sessionInflight) {
    sessionInflight = (async () => {
      let u = await getSessionOnce();
      if(!u.authenticated && readSessionCache()?.authenticated) {
        await new Promise((r) => setTimeout(r, 250));
        u = await getSessionOnce();
      }
      if(u.authenticated) bumpSessionCache();
      else clearSessionCache();
      return u;
    })().finally(() => { sessionInflight = null; });
  }
  return sessionInflight;
}

let categoriesCache: Category[] | null = null;
let categoriesInflight: Promise<Category[]> | null = null;

export function getCategoriesCached(): Promise<Category[]> {
  if(categoriesCache) return Promise.resolve(categoriesCache);
  if(!categoriesInflight) {
    categoriesInflight = getCategories().then((r) => {
      categoriesCache = r.items;
      return r.items;
    }).finally(() => { categoriesInflight = null; });
  }
  return categoriesInflight;
}

export function invalidateCategoriesCache() {
  categoriesCache = null;
}

let homeBannersCache: HomeBannerCategory[] | null = null;
let homeBannersInflight: Promise<HomeBannerCategory[]> | null = null;

export function getHomeBannerCategoriesCached(): Promise<HomeBannerCategory[]> {
  if(homeBannersCache) return Promise.resolve(homeBannersCache);
  if(!homeBannersInflight) {
    homeBannersInflight = getHomeBannerCategories().then((r) => {
      homeBannersCache = r.items;
      return r.items;
    }).finally(() => { homeBannersInflight = null; });
  }
  return homeBannersInflight;
}

export function prefetchHomeData(): void {
  getCategoriesCached().catch(() => {});
  getHomeBannerCategoriesCached().catch(() => {});
}

let wishCountCache: number | null = null;
let wishInflight: Promise<number> | null = null;

export function getWishCountCached(sessionUser?: SessionUser | null): Promise<number> {
  if(sessionUser?.wishlistCount != null) {
    wishCountCache = sessionUser.wishlistCount;
    return Promise.resolve(sessionUser.wishlistCount);
  }
  if(wishCountCache != null) return Promise.resolve(wishCountCache);
  if(!wishInflight) {
    wishInflight = getWishlist().then((r) => {
      wishCountCache = r.count;
      return r.count;
    }).catch(() => 0).finally(() => { wishInflight = null; });
  }
  return wishInflight;
}

export function setWishCountCache(n: number) {
  wishCountCache = n;
}

type SiteStatusCache = {status: ShopSiteStatus; ts: number};

export function readSiteStatusCache(): ShopSiteStatus | null {
  if(typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(SITE_STATUS_KEY);
    if(!raw) return null;
    const parsed = JSON.parse(raw) as SiteStatusCache;
    if(!parsed?.status || Date.now() - parsed.ts > SITE_STATUS_TTL) return null;
    return parsed.status;
  } catch {
    return null;
  }
}

export function writeSiteStatusCache(status: ShopSiteStatus) {
  if(typeof window === "undefined") return;
  try {
    localStorage.setItem(SITE_STATUS_KEY, JSON.stringify({status, ts: Date.now()} satisfies SiteStatusCache));
  } catch {}
}
